import { useState, useEffect } from 'react';
import { challenges, weeklySpecialChallenges } from '../data/challenges';
import * as storage from '../utils/storage';

/**
 * Manages challenge state, generation, and the weekly expiry effect.
 *
 * @param {{ logs: Array, setLogs: Function, currentTime: number }} params
 *   - logs/setLogs: owned by App.js; needed by the weekly auto-bitch effect
 *   - currentTime: the minute-tick clock from App.js; used by getTimeRemaining
 */
export const useChallenges = ({ logs, setLogs, currentTime }) => {
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [weeklyChallenge, setWeeklyChallenge] = useState(null);
  const [weeklyCompleted, setWeeklyCompleted] = useState(false);
  const [weeklyStartDate, setWeeklyStartDate] = useState(null);
  const [daysUntilNextWeekly, setDaysUntilNextWeekly] = useState(0);
  const [dailyChallengeCount, setDailyChallengeCount] = useState(0);
  const [weeklyChallengeCount, setWeeklyChallengeCount] = useState(0);
  const [rerollTokens, setRerollTokens] = useState(0);
  const [maxRerollTokens] = useState(3);
  const [showRerollAnimation, setShowRerollAnimation] = useState(false);

  const loadChallengeCompletionCount = async (type, challengeText) => {
    const count = storage.getLocalChallengeCount(type, challengeText);
    if (type === 'daily') {
      setDailyChallengeCount(count);
    } else if (type === 'weekly') {
      setWeeklyChallengeCount(count);
    }
  };

  const incrementChallengeCount = async (type, challengeText) => {
    const newCount = storage.incrementLocalChallengeCount(type, challengeText);
    if (type === 'daily') {
      setDailyChallengeCount(newCount);
    } else if (type === 'weekly') {
      setWeeklyChallengeCount(newCount);
    }
  };

  // communityPicks: array of promoted challenge_submissions from Supabase
  // 20% chance to serve a community pick when at least one exists
  const generateNewDailyChallenge = (communityPicks = []) => {
    let newChallenge;

    const useCommunityPick =
      communityPicks.length > 0 && Math.random() < 0.20;

    if (useCommunityPick) {
      const pick = communityPicks[Math.floor(Math.random() * communityPicks.length)];
      newChallenge = {
        text: pick.text,
        points: pick.points || 5,
        category: pick.category,
        completed: false,
        isCommunityPick: true,
        submissionId: pick.id,
        submitterId: pick.submitter_id,
        submitterUsername: pick.submitter_username,
      };
    } else {
      const categories = Object.keys(challenges);
      const randomCategory = categories[Math.floor(Math.random() * categories.length)];
      const categoryList = challenges[randomCategory];
      const randomChallenge = categoryList[Math.floor(Math.random() * categoryList.length)];
      newChallenge = {
        text: randomChallenge.text,
        points: randomChallenge.points,
        category: randomCategory,
        completed: false,
      };
    }

    setDailyChallenge(newChallenge);
    storage.setDailyChallenge({
      challenge: newChallenge,
      date: new Date()
    });

    // Load count for new challenge
    loadChallengeCompletionCount('daily', newChallenge.text);
  };

  const generateNewWeeklyChallenge = () => {
    const weeklyIndex = Math.floor(Math.random() * weeklySpecialChallenges.length);
    const weeklySelected = weeklySpecialChallenges[weeklyIndex];
    const newWeekly = {
      text: weeklySelected.text,
      points: weeklySelected.points,
      category: weeklySelected.category
    };
    const startDate = Date.now();

    setWeeklyChallenge(newWeekly);
    setWeeklyCompleted(false);
    setWeeklyStartDate(startDate);
    storage.setWeeklyChallenge(newWeekly);
    storage.setWeeklyCompleted(false);
    storage.setWeeklyStartDate(startDate);

    // Load count for new challenge
    loadChallengeCompletionCount('weekly', newWeekly.text);
  };

  const getTimeRemaining = (challengeType) => {
    if (challengeType === 'daily') {
      const saved = storage.getDailyChallenge();
      if (!saved) return null;
      const challengeTime = new Date(saved.date).getTime();
      const deadline = challengeTime + (24 * 60 * 60 * 1000);
      const remaining = deadline - currentTime;

      if (remaining <= 0) return { expired: true };

      const hours = Math.floor(remaining / (60 * 60 * 1000));
      const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

      return {
        hours,
        minutes,
        isWarning: hours < 6,
        totalMs: remaining
      };
    } else if (challengeType === 'weekly') {
      if (!weeklyStartDate) return null;

      const deadline = weeklyStartDate + (7 * 24 * 60 * 60 * 1000);
      const remaining = deadline - currentTime;

      if (remaining <= 0) return { expired: true };

      const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
      const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));

      return {
        days,
        hours,
        isWarning: days === 0 && hours < 6,
        totalMs: remaining
      };
    }
    return null;
  };

  // Update weekly challenge days remaining live; auto-bitch if week expired
  useEffect(() => {
    if (!weeklyStartDate) return;

    const updateDays = () => {
      const now = Date.now();
      const deadline = weeklyStartDate + 7 * 24 * 3600 * 1000;
      const remaining = deadline - now;
      if (remaining <= 0) {
        setDaysUntilNextWeekly(0);
        // Auto-regenerate weekly challenge if week has passed
        if (!weeklyCompleted) {
          const autoBitchLog = {
            id: Date.now(),
            challenge: weeklyChallenge?.text || 'Weekly challenge expired',
            points: weeklyChallenge?.points || 0,
            earnedPoints: 0,
            recoveryBonus: false,
            category: weeklyChallenge?.category || 'weekly',
            success: false,
            outcome: null,
            timestamp: now,
            date: new Date().toLocaleDateString(),
            autoBitched: true,
            isWeekly: true
          };

          const updatedLogs = [autoBitchLog, ...logs];
          setLogs(updatedLogs);
          storage.setLogs(updatedLogs);
        }
        // Generate new weekly challenge regardless of completion status
        generateNewWeeklyChallenge();
      } else {
        setDaysUntilNextWeekly(Math.ceil(remaining / (24 * 3600 * 1000)));
      }
    };

    // Run immediately
    updateDays();

    // Then update every minute
    const intervalId = setInterval(updateDays, 60000);

    // Cleanup on unmount or when deps change
    return () => clearInterval(intervalId);
  }, [weeklyStartDate, weeklyCompleted, weeklyChallenge, logs]);

  return {
    dailyChallenge, setDailyChallenge,
    weeklyChallenge, setWeeklyChallenge,
    weeklyCompleted, setWeeklyCompleted,
    weeklyStartDate, setWeeklyStartDate,
    daysUntilNextWeekly, setDaysUntilNextWeekly,
    dailyChallengeCount, setDailyChallengeCount,
    weeklyChallengeCount, setWeeklyChallengeCount,
    rerollTokens, setRerollTokens,
    maxRerollTokens,
    showRerollAnimation, setShowRerollAnimation,
    generateNewDailyChallenge,
    generateNewWeeklyChallenge,
    loadChallengeCompletionCount,
    incrementChallengeCount,
    getTimeRemaining,
  };
};
